<template>
    <pt-popper
        ref="refPopper"
        v-model:visible="pickerVisible"
        manual-mode
        effect="light"
        pure
        trigger="click"
        v-bind="$attrs"
        :popper-class="`pt-picker__popper ${popperClass}`"
        :popper-options="ptPopperOptions"
        :fallback-placements="['bottom', 'top', 'right', 'left']"
        transition="pt-zoom-in-top"
        :gpu-acceleration="false"
        :stop-popper-mouse-event="false"
        append-to-body
        @before-enter="pickerActualVisible = true"
        @after-leave="pickerActualVisible = false"
    >
        <template #trigger>
            <div
                v-clickoutside:[popperPaneRef]="onClickOutside"
                class="pt-date-editor pt-range-editor pt-input__inner"
                :class="[
                    'pt-date-editor--' + type,
                    pickerSize ? `pt-range-editor--${pickerSize}` : '',
                    pickerDisabled ? 'is-disabled' : '',
                ]"
                @click="handleFocus"
                @mouseenter="onMouseEnter"
                @keydown="handleKeydown"
            >
                <pt-icon
                    icon="pt-icon-datepicker"
                    :class="['pt-input__icon', 'pt-icon_clock-v2', 'pt-range__icon']"
                    @click="handleFocus"
                ></pt-icon>
                <span :placeholder="startPlaceholder" class="pt-range-span">
                    {{ displayValue && displayValue[0] }}</span
                >
                <slot name="range-separator">
                    <span class="pt-date-separator">{{ rangeSeparator }}</span>
                </slot>

                <span :placeholder="startPlaceholder" class="pt-range-span">
                    {{ displayValue && displayValue[1] }}</span
                >
                <pt-icon icon="pt-icon-arrow-16" class="pt-range-select"></pt-icon>
            </div>
        </template>
        <template #default>
            <slot
                :visible="pickerVisible"
                :actual-visible="pickerActualVisible"
                :parsed-value="parsedValue"
                :format="format"
                :unlink-panels="unlinkPanels"
                :defaultShortcut="defaultShortcut"
                :timeZone="timeZone"
                :type="type"
                :default-value="defaultValue"
                @pick="onPick"
                @close="onClickOutside"
                @select-range="setSelectionRange"
                @set-picker-option="onSetPickerOption"
                @mousedown.stop
            ></slot>
        </template>
    </pt-popper>
</template>
<script lang="ts">
import { defineComponent, ref, computed, nextTick, inject, watch, provide } from 'vue';
import dayjs, { Dayjs } from 'dayjs';
import isEqual from 'lodash-es/isEqual.js';
import { ClickOutside } from '../../../directives';
import PtInput from '../../../input';
import PtPopper from '../../../popper';
import { EVENT_CODE } from '../../../../utils/aria';
import { useGlobalConfig, isEmpty } from '../../../../utils/util';
import { elFormKey, elFormItemKey } from '../../../form';
import { defaultProps } from './props';
import type { PtFormContext, PtFormItemContext } from '../../../form';
import type { Options } from '@popperjs/core';

interface PickerOptions {
    isValidValue: any;
    handleKeydown: any;
    parseUserInput: any;
    formatToString: any;
    getRangeAvailableTime: any;
    getDefaultValue: any;
    panelReady: boolean;
    handleClear: any;
}
// Date object and string
const dateEquals = function (a, b) {
    const aIsDate = a instanceof Date;
    const bIsDate = b instanceof Date;
    if (aIsDate && bIsDate) {
        return a.getTime() === b.getTime();
    }
    if (!aIsDate && !bIsDate) {
        return a === b;
    }
    return false;
};

const valueEquals = function (a, b) {
    const aIsArray = a instanceof Array;
    const bIsArray = b instanceof Array;
    if (aIsArray && bIsArray) {
        if (a.length !== b.length) {
            return false;
        }
        return a.every((item, index) => dateEquals(item, b[index]));
    }
    if (!aIsArray && !bIsArray) {
        return dateEquals(a, b);
    }
    return false;
};

const parser = function (date: Date | string, format: string): Dayjs {
    const day = isEmpty(format) ? dayjs(date) : dayjs(date, format);
    return day.isValid() ? day : undefined;
};

const formatter = function (date: Date, format: string, timeZone: string) {
    return isEmpty(format) ? date : timeZone ? dayjs(date).tz(timeZone).format(format) : dayjs(date).format(format);
};

export default defineComponent({
    name: 'Picker',
    components: {
        PtInput,
        PtPopper,
    },
    directives: { clickoutside: ClickOutside },
    props: defaultProps,
    emits: ['update:modelValue', 'change', 'focus', 'blur'],
    setup(props, ctx) {
        const ELEMENT = useGlobalConfig();

        const elForm = inject(elFormKey, {} as PtFormContext);
        const elFormItem = inject(elFormItemKey, {} as PtFormItemContext);
        const ptPopperOptions = inject('PtPopperOptions', {} as Options);

        const refPopper = ref(null);
        const pickerVisible = ref(false);
        const pickerActualVisible = ref(false);
        const valueOnOpen = ref(null);
        const defaultShortcut = props.defaultShortcut;
        const timeZone = props.timeZone;

        watch(pickerVisible, (val) => {
            if (!val) {
                userInput.value = null;
                nextTick(() => {
                    emitChange(props.modelValue);
                });
                ctx.emit('blur');
                blurInput();
                props.validateEvent && elFormItem.formItemMitt?.emit('el.form.blur');
            } else {
                valueOnOpen.value = props.modelValue;
            }
        });
        const emitChange = (val, isClear?: boolean) => {
            // determine user real change only
            if (isClear || !valueEquals(val, valueOnOpen.value)) {
                ctx.emit('change', val);
                props.validateEvent && elFormItem.formItemMitt?.emit('el.form.change', val);
            }
        };
        const emitInput = (val) => {
            if (!valueEquals(props.modelValue, val)) {
                let formatValue;
                if (Array.isArray(val)) {
                    formatValue = val.map((_) => formatter(_, props.valueFormat, props.timeZone));
                } else if (val) {
                    formatValue = formatter(val, props.valueFormat, props.timeZone);
                }
                ctx.emit('update:modelValue', val ? formatValue : val);
            }
        };
        const refInput = computed(() => {
            if (refPopper.value.triggerRef) {
                const _r = isRangeInput.value ? refPopper.value.triggerRef : refPopper.value.triggerRef.$el;
                return [].slice.call(_r.querySelectorAll('input'));
            }
            return [];
        });
        const setSelectionRange = (start, end, pos) => {
            const _inputs = refInput.value;
            if (!_inputs.length) return;
            if (!pos || pos === 'min') {
                _inputs[0].setSelectionRange(start, end);
                _inputs[0].focus();
            } else if (pos === 'max') {
                _inputs[1].setSelectionRange(start, end);
                _inputs[1].focus();
            }
        };
        const onPick = (date: any = '', visible = false) => {
            pickerVisible.value = visible;
            let result;
            if (Array.isArray(date)) {
                result = date.map((_) => _.toDate());
            } else {
                // clear btn emit null
                result = date ? date.toDate() : date;
            }
            userInput.value = null;
            emitInput(result);
        };
        const handleFocus = (e) => {
            if (props.readonly || pickerDisabled.value || pickerVisible.value) return;
            pickerVisible.value = true;
            ctx.emit('focus', e);
        };

        const handleBlur = () => {
            pickerVisible.value = false;
            blurInput();
        };

        const pickerDisabled = computed(() => {
            return props.disabled || elForm.disabled;
        });

        const parsedValue = computed(() => {
            let result;
            if (valueIsEmpty.value) {
                if (pickerOptions.value.getDefaultValue) {
                    result = pickerOptions.value.getDefaultValue();
                }
            } else {
                if (Array.isArray(props.modelValue)) {
                    result = props.modelValue.map((_) => parser(_, props.valueFormat));
                } else {
                    result = parser(props.modelValue, props.valueFormat);
                }
            }

            if (pickerOptions.value.getRangeAvailableTime) {
                const availableResult = pickerOptions.value.getRangeAvailableTime(result);
                if (!isEqual(availableResult, result)) {
                    result = availableResult;
                    emitInput(Array.isArray(result) ? result.map((_) => _.toDate()) : result.toDate());
                }
            }
            if (Array.isArray(result) && result.some((_) => !_)) {
                result = [];
            }
            // if (timeZone) {
            //     result = result.map((_) => dayjs(_).tz(timeZone, true));
            // }

            return result;
        });

        const displayValue = computed(() => {
            if (!pickerOptions.value.panelReady) return;
            const formattedValue = formatDayjsToString(parsedValue.value);
            if (Array.isArray(userInput.value)) {
                return [
                    userInput.value[0] || (formattedValue && formattedValue[0]) || '',
                    userInput.value[1] || (formattedValue && formattedValue[1]) || '',
                ];
            } else if (userInput.value !== null) {
                return userInput.value;
            }
            if (!isTimePicker.value && valueIsEmpty.value) return;
            if (!pickerVisible.value && valueIsEmpty.value) return;
            if (formattedValue) {
                return isDatesPicker.value ? (formattedValue as Array<string>).join(', ') : formattedValue;
            }
            return '';
        });

        const isTimeLikePicker = computed(() => {
            return props.type.indexOf('time') !== -1;
        });

        const isTimePicker = computed(() => {
            return props.type.indexOf('time') === 0;
        });

        const isDatesPicker = computed(() => {
            return props.type === 'dates';
        });

        const triggerClass = computed(() => {
            return props.prefixIcon || (isTimeLikePicker.value ? 'pt-icon-clock' : 'pt-icon-datepicker');
        });
        const valueIsEmpty = computed(() => {
            return !props.modelValue || (Array.isArray(props.modelValue) && !props.modelValue.length);
        });
        const onMouseEnter = () => {
            if (props.readonly || pickerDisabled.value) return;
        };
        const isRangeInput = ref(true);

        const pickerSize = computed(() => {
            return props.size || elFormItem.size || ELEMENT.size;
        });

        const popperPaneRef = computed(() => {
            return refPopper.value?.popperRef;
        });

        const onClickOutside = () => {
            if (!pickerVisible.value) return;
            pickerVisible.value = false;
        };

        const userInput = ref(null);

        const handleChange = () => {
            if (userInput.value) {
                const value = parseUserInputToDayjs(displayValue.value);
                if (value) {
                    if (isValidValue(value)) {
                        emitInput(Array.isArray(value) ? value.map((_) => _.toDate()) : value.toDate());
                        userInput.value = null;
                    }
                }
            }
            if (userInput.value === '') {
                emitInput(null);
                emitChange(null);
                userInput.value = null;
            }
        };

        const blurInput = () => {
            refInput.value.forEach((input) => input.blur());
        };

        const parseUserInputToDayjs = (value) => {
            if (!value) return null;
            return pickerOptions.value.parseUserInput(value);
        };

        const formatDayjsToString = (value) => {
            if (!value) return null;
            return pickerOptions.value.formatToString(value);
        };

        const isValidValue = (value) => {
            return pickerOptions.value.isValidValue(value);
        };

        const handleKeydown = (event) => {
            const code = event.code;

            if (code === EVENT_CODE.esc) {
                pickerVisible.value = false;
                event.stopPropagation();
                return;
            }

            if (code === EVENT_CODE.tab) {
                if (!isRangeInput.value) {
                    handleChange();
                    pickerVisible.value = false;
                    event.stopPropagation();
                } else {
                    // user may change focus between two input
                    setTimeout(() => {
                        if (refInput.value.indexOf(document.activeElement) === -1) {
                            pickerVisible.value = false;
                            blurInput();
                        }
                    }, 0);
                }
                return;
            }

            if (code === EVENT_CODE.enter) {
                if (userInput.value === '' || isValidValue(parseUserInputToDayjs(displayValue.value))) {
                    handleChange();
                    pickerVisible.value = false;
                }
                event.stopPropagation();
                return;
            }

            // if user is typing, do not let picker handle key input
            if (userInput.value) {
                event.stopPropagation();
                return;
            }

            if (pickerOptions.value.handleKeydown) {
                pickerOptions.value.handleKeydown(event);
            }
        };
        const onUserInput = (e) => {
            userInput.value = e;
        };

        const handleStartInput = (event) => {
            if (userInput.value) {
                userInput.value = [event.target.value, userInput.value[1]];
            } else {
                userInput.value = [event.target.value, null];
            }
        };

        const handleEndInput = (event) => {
            if (userInput.value) {
                userInput.value = [userInput.value[0], event.target.value];
            } else {
                userInput.value = [null, event.target.value];
            }
        };

        const handleStartChange = () => {
            const value = parseUserInputToDayjs(userInput.value && userInput.value[0]);
            if (value && value.isValid()) {
                userInput.value = [formatDayjsToString(value), displayValue.value[1]];
                const newValue = [value, parsedValue.value && parsedValue.value[1]];
                if (isValidValue(newValue)) {
                    emitInput(newValue);
                    userInput.value = null;
                }
            }
        };

        const handleEndChange = () => {
            const value = parseUserInputToDayjs(userInput.value && userInput.value[1]);
            if (value && value.isValid()) {
                userInput.value = [displayValue.value[0], formatDayjsToString(value)];
                const newValue = [parsedValue.value && parsedValue.value[0], value];
                if (isValidValue(newValue)) {
                    emitInput(newValue);
                    userInput.value = null;
                }
            }
        };

        const pickerOptions = ref({} as PickerOptions);
        const onSetPickerOption = (e) => {
            pickerOptions.value[e[0]] = e[1];
            pickerOptions.value.panelReady = true;
        };

        provide('EP_PICKER_BASE', {
            props,
        });
        return {
            // injected popper options
            ptPopperOptions,

            isDatesPicker,
            handleEndChange,
            handleStartChange,
            handleStartInput,
            handleEndInput,
            onUserInput,
            handleChange,
            handleKeydown,
            popperPaneRef,
            onClickOutside,
            pickerSize,
            isRangeInput,
            onMouseEnter,
            triggerClass,
            onPick,
            handleFocus,
            handleBlur,
            pickerVisible,
            pickerActualVisible,
            displayValue,
            parsedValue,
            setSelectionRange,
            refPopper,
            pickerDisabled,
            onSetPickerOption,
            defaultShortcut,
            timeZone,
        };
    },
});
</script>
